import { ref } from 'vue';
import { ServerErrorsType } from '@/types/Validation';
import { ServerBaseDataOnError } from '@/types/Response';
import i18n from '@/locale';

export function useValidation() {
    const serverErrors = ref<ServerErrorsType>({});
    const validationActiveArray = ref<string[]>([]);

    type RuleError = { key: string, params?: Record<string, number> };

    const applyRule = (value: any, rule: string): RuleError | null => {
        const [ruleName, ruleValue] = rule.split(':');

        switch (ruleName) {
            case 'required':
                if (!value || (typeof value === 'string' && value.trim() === '')) {
                    return { key: 'VALIDATION.FIELD.REQUIRED' };
                }
                break;
            case 'email':
                const emailRegex = /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/;
                if (value && !emailRegex.test(value)) {
                    return { key: 'VALIDATION.FIELD.EMAIL' };
                }
                break;
            case 'passwordMin':
                if (value && value.length < 6) {
                    return { key: 'VALIDATION.FIELD.PASSWORD.MIN', params: { length: 6 }};
                }
                break;
            case 'minLength':
                const minLength = parseInt(ruleValue, 10);
                if (value && value.length < minLength) {
                    return { key: 'VALIDATION.FIELD.CHARACTERS.MIN', params: { length: minLength }};
                }
                break;
            case 'maxLength':
                const maxLength = parseInt(ruleValue, 10);
                if (value && value.length > maxLength) {
                    return { key: 'VALIDATION.FIELD.CHARACTERS.MAX', params: { length: maxLength }};
                }
                break;
            case 'length':
                const exactLength = parseInt(ruleValue, 10);
                if (value && value.length !== exactLength) {
                    return { key: 'VALIDATION.FIELD.CHARACTERS.EXACT', params: { length: exactLength }};
                }
                break;
            default:
                break;
        }
        return null;
    };

    const validateField = (value: any, rules: string[]): boolean => {
        for (const rule of rules) {
            const error = applyRule(value, rule);
            if (error) {
                return false;
            }
        }
        return true;
    };

    const clearServerErrorsBase = (fieldName: string): void => {
        serverErrors.value[fieldName] = null;
    };

    const addCurrentInputToValidateArray = (fieldName: string): void => {
        if (!validationActiveArray.value.includes(fieldName)) {
            validationActiveArray.value.push(fieldName);
        }
    };

    const getFieldErrorTextByRule = (value: any, name: string, rules: Array<string>): string | null => {
        if (!validationActiveArray.value.includes(name)) {
            return null;
        }
        if (serverErrors.value[name]) {
            return serverErrors.value[name];
        }
        for (const rule of rules) {
            const error = applyRule(value, rule);
            if (error) {
                return i18n.global.t(error.key, error.params || {});
            }
        }
        return null;
    };

    const fieldHasErrorByRule = (value: any, name: string, rules: Array<string>): boolean => {
        if (!validationActiveArray.value.includes(name)) {
            return false;
        }
        if (serverErrors.value[name]) {
            return true;
        }
        for (const rule of rules) {
            if (applyRule(value, rule)) {
                return true;
            }
        }
        return false;
    };

    const fieldHasError = (fieldName: string): boolean => {
        return Boolean(
            validationActiveArray.value.includes(fieldName) &&
      (serverErrors.value[fieldName] || false)
        );
    };

    const fieldHasNotError = (fieldName: string): boolean => {
        return Boolean(
            validationActiveArray.value.includes(fieldName) &&
      !serverErrors.value[fieldName]
        );
    };

    const getFieldErrorText = (fieldName: string): string | null => {
        if (!validationActiveArray.value.includes(fieldName)) {
            return null;
        }
        return serverErrors.value[fieldName] || null;
    };

    const prepareServerFormErrors = (data: ServerBaseDataOnError): ServerErrorsType => {
        if (!data.errors) {
            return {};
        }

        function getFieldErrorNameFromString(errorType: string): string | null {
            const subStringArray = errorType.split('.', 2);
            const index: number = subStringArray.indexOf('user');
            if (index !== -1) subStringArray.splice(index, 1);
            return subStringArray && subStringArray.length ? subStringArray[0] : null;
        }

        const errors: ServerErrorsType = {};
        for (let i = 0; i < data.errors.length; i++) {
            const field = getFieldErrorNameFromString(data.errors[i].type);
            if (field) {
                errors[field] = data.errors[i].text;
            }
        }
        return errors;
    };

    const emailValidator = ({ value }: { value: string }): boolean => {
        return validateField(value, ['required', 'email']);
    };

    const baseInputValidator = ({ value, minLength = 2 }: { value: string, minLength?: number }): boolean => {
        return validateField(value, ['required', `minLength:${minLength}`, 'maxLength:100']);
    };

    const requiredValidator = ({ value }: { value: string }): boolean => {
        return validateField(value, ['required']);
    };

    const passwordRequirements = ['required', 'passwordMin'];

    const passwordValidator = ({ value }: { value: string }): boolean => {
        return validateField(value, passwordRequirements);
    };

    const baseTextAreaValidator = ({ value, minLength = 2 }: { value: string, minLength?: number }): boolean => {
        return validateField(value, ['required', `minLength:${minLength}`, 'maxLength:500']);
    };

    const validateToken = ({ value }: { value: string }): boolean => {
        return validateField(value, ['required', 'length:5']);
    };

    const validatePasswordMatch = ({ password, passwordNew }: { password: string, passwordNew: string }): boolean => {
        return password === passwordNew;
    };

    const propsKeyDownMethod = ($event: KeyboardEvent) => {
        if (!Number.isInteger(parseInt($event.key, 10)) &&
        $event.key !== 'Tab' &&
        $event.key !== 'Backspace' &&
        !($event.ctrlKey && $event.key === 'v' || $event.key === 'c') &&
        !($event.metaKey && $event.key === 'v' || $event.key === 'c')) {
            $event.preventDefault();
        }
    };

    return {
        serverErrors,
        validationActiveArray,
        clearServerErrorsBase,
        addCurrentInputToValidateArray,
        fieldHasError,
        fieldHasNotError,
        getFieldErrorText,
        getFieldErrorTextByRule,
        fieldHasErrorByRule,
        prepareServerFormErrors,
        emailValidator,
        baseInputValidator,
        requiredValidator,
        passwordValidator,
        baseTextAreaValidator,
        validateToken,
        validatePasswordMatch,
        passwordRequirements,
        propsKeyDownMethod,
    };
}
